import { deleteDoc, onSnapshot, orderBy, query, setDoc, where } from 'firebase/firestore'
import { trackerDayRef, trackerDaysRef, trackersRef } from './paths'
import {
  normaliseTrackerDay,
  normaliseTrackers,
  trackerDayId,
  type Tracker,
  type TrackerDay,
} from './trackers'
import type { DayKey } from '../lib/time'

/** The tracker list. One settings document, like the taxonomy. */
export function subscribeTrackers(
  uid: string,
  onChange: (trackers: Tracker[]) => void,
  onError?: (error: Error) => void,
): () => void {
  return onSnapshot(
    trackersRef(uid),
    (snapshot) => onChange(normaliseTrackers(snapshot.data())),
    (error) => onError?.(error),
  )
}

export async function writeTrackers(uid: string, trackers: Tracker[]): Promise<void> {
  await setDoc(trackersRef(uid), { trackers })
}

/**
 * Every logged day in a range, for every tracker at once. The day key is a
 * sortable string, so one range query covers the whole heatmap without a
 * compound index — which is what keeps the overall view a single read.
 */
export function subscribeTrackerDays(
  uid: string,
  from: DayKey,
  to: DayKey,
  onChange: (days: TrackerDay[]) => void,
  onError?: (error: Error) => void,
): () => void {
  return onSnapshot(
    query(trackerDaysRef(uid), where('day', '>=', from), where('day', '<=', to), orderBy('day')),
    (snapshot) => {
      const days: TrackerDay[] = []
      for (const doc of snapshot.docs) {
        const day = normaliseTrackerDay(doc.data())
        if (day) days.push(day)
      }
      onChange(days)
    },
    (error) => onError?.(error),
  )
}

/**
 * Set a day's count outright. Writing zero removes the document rather than
 * storing a zero: a day with nothing logged and a day that was never logged are
 * the same thing, and keeping both would make the heatmap lie about coverage.
 */
export async function writeTrackerDay(
  uid: string,
  trackerId: string,
  day: DayKey,
  count: number,
  times: number[],
): Promise<void> {
  const id = trackerDayId(trackerId, day)
  if (count <= 0) {
    await deleteDoc(trackerDayRef(uid, id))
    return
  }
  await setDoc(trackerDayRef(uid, id), { trackerId, day, count, times })
}
